import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faArrowRight, faArrowLeft, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Hero from '../components/Hero';
import projectsHeroImage from '../assets/images/projects.jpg';
import './SoftwareCostCalculator.css';

const TEXTS = {
  tr: {
    heroTitle: 'YAZILIM MALİYET HESAPLAYICI',
    pageTitle: 'Yazılım Maliyet Hesaplayıcı | ProgenX',
    metaDesc: 'ProgenX yazılım proje maliyet hesaplayıcı. Web, mobil, oyun projelerinizin tahmini bütçesini hesaplayın.',
    introTitle: 'Yazılım Projenizin Maliyetini Hesaplayın',
    introDesc: 'Birkaç basit adımda projenizin tahmini bütçesini öğrenin. Proje tipinizi, özelliklerinizi ve ihtiyaçlarınızı seçin.',
    startBtn: 'Hesaplamaya Başla',
    step: 'Adım',
    next: 'Devam',
    back: 'Geri',
    calculate: 'Hesapla',
    loadingTitle: 'Projeniz Analiz Ediliyor...',
    loadingDesc: 'En doğru tahmin için verileriniz işleniyor.',
    resultTitle: 'Tahmini Proje Bütçeniz',
    resultNote: 'Bu tahmin, seçimlerinize göre otomatik hesaplanmıştır. Kesin fiyat proje detaylarına göre değişebilir.',
    contactTitle: 'Detaylı Teklif Alın',
    contactDesc: 'Bilgilerinizi bırakın, size özel detaylı teklif hazırlayalım.',
    nameLabel: 'Adınız Soyadınız',
    emailLabel: 'E-posta Adresiniz',
    phoneLabel: 'Telefon Numaranız',
    messageLabel: 'Proje Hakkında Not (Opsiyonel)',
    sendBtn: 'Teklif İste',
    successTitle: 'Talebiniz Alındı!',
    successDesc: 'En kısa sürede size dönüş yapacağız.',
    restart: 'Yeniden Hesapla',
    steps: {
      projectType: { label: 'Proje Tipi', title: 'Proje Tipinizi Seçin' },
      domain: { label: 'Sektör', title: 'Projenizin Sektörünü Seçin' },
      mobilePlatform: { label: 'Platform', title: 'Mobil Platformu Seçin' },
      pageCount: { label: 'Sayfa Sayısı', title: 'Sayfa Sayısını Belirleyin' },
      gameType: { label: 'Oyun Tipi', title: 'Oyun Tipini Seçin' },
      designLevel: { label: 'Tasarım', title: 'Tasarım Seviyesini Seçin' },
      features: { label: 'Özellikler', title: 'İhtiyacınız Olan Özellikleri Seçin' },
    },
    projectTypes: [
      { id: 'web', name: 'Web Uygulaması', price: 300 },
      { id: 'mobile', name: 'Mobil Uygulama', price: 1500 },
      { id: 'game', name: 'Oyun', price: 5000 },
      { id: 'mobile-game', name: 'Mobil Oyun', price: 4000 },
      { id: 'both', name: 'Web + Mobil', price: 1800 },
    ],
    domains: [
      { id: 'general', name: 'Genel', price: 0 },
      { id: 'education', name: 'Eğitim', price: 300 },
      { id: 'iot', name: 'IoT', price: 1000 },
      { id: 'dashboard', name: 'Dashboard', price: 600 },
      { id: 'ecommerce', name: 'E-Ticaret', price: 1000 },
      { id: 'finance', name: 'Finans', price: 1500 },
      { id: 'healthcare', name: 'Sağlık', price: 1500 },
      { id: 'social', name: 'Sosyal Medya', price: 600 },
      { id: 'cms', name: 'İçerik Yönetimi', price: 400 },
      { id: 'hr', name: 'İnsan Kaynakları', price: 400 },
      { id: 'crm', name: 'CRM', price: 400 },
      { id: 'project-management', name: 'Proje Yönetimi', price: 500 },
      { id: 'restaurant', name: 'Restoran', price: 200 },
      { id: 'logistics', name: 'Lojistik', price: 1000 },
    ],
    mobilePlatforms: [
      { id: 'ios', name: 'iOS', price: 1000 },
      { id: 'android', name: 'Android', price: 500 },
      { id: 'both', name: 'iOS + Android', price: 1300 },
    ],
    pageCounts: [
      { id: '1-5', name: '1-5 Sayfa', price: 0 },
      { id: '6-10', name: '6-10 Sayfa', price: 100 },
      { id: '11-20', name: '11-20 Sayfa', price: 200 },
      { id: '21-50', name: '21-50 Sayfa', price: 300 },
      { id: '50+', name: '50+ Sayfa', price: 1000 },
    ],
    gameTypes: [
      { id: '2d', name: '2D Oyun', price: 5000 },
      { id: '3d', name: '3D Oyun', price: 10000 },
      { id: 'casual', name: 'Casual Oyun', price: 4000 },
      { id: 'strategy', name: 'Strateji Oyunu', price: 8000 },
    ],
    designLevels: [
      { id: 'basic', name: 'Temel Tasarım', price: 100 },
      { id: 'standard', name: 'Standart Tasarım', price: 200 },
      { id: 'premium', name: 'Premium Tasarım', price: 300 },
      { id: 'custom', name: 'Özel Tasarım', price: 5000 },
    ],
    featuresList: [
      { id: 'payment', name: 'Ödeme Entegrasyonu', price: 200 },
      { id: 'auth', name: 'Kimlik Doğrulama', price: 100 },
      { id: 'integration', name: 'API Entegrasyonu', price: 1200 },
      { id: 'reporting', name: 'Raporlama', price: 500 },
      { id: 'chat', name: 'Canlı Sohbet', price: 300 },
      { id: 'notification', name: 'Bildirimler', price: 300 },
      { id: 'analytics', name: 'Analitik', price: 600 },
      { id: 'video-streaming', name: 'Video Streaming', price: 1500 },
      { id: 'certificate', name: 'Sertifika Sistemi', price: 200 },
      { id: 'gamification', name: 'Oyunlaştırma', price: 200 },
      { id: 'iot-device', name: 'IoT Cihaz Yönetimi', price: 1000 },
      { id: 'real-time', name: 'Gerçek Zamanlı Veri', price: 1500 },
    ],
    seo: {
      whatIsTitle: 'Yazılım Maliyet Hesaplayıcı Nedir?',
      whatIsP1: 'Yazılım maliyet hesaplayıcı, bir yazılım projesinin tahmini bütçesini belirlemek için kullanılan interaktif bir araçtır. Proje tipi, sektör, platform tercihi, tasarım seviyesi ve ihtiyaç duyulan özellikler gibi parametreleri analiz ederek projenizin yaklaşık maliyetini hesaplar.',
      whatIsP2: 'ProgenX yazılım maliyet hesaplayıcı, 2026 yılı Türkiye ve global yazılım piyasası fiyatlarını baz alarak web uygulaması, mobil uygulama, oyun geliştirme ve kurumsal yazılım projeleriniz için detaylı bütçe tahmini sunar. Eğitim yazılımı, e-ticaret, sağlık teknolojileri, fintech, IoT ve lojistik gibi 14 farklı sektör için özelleştirilmiş fiyatlandırma yapılmaktadır.',
      howWorksTitle: 'Yazılım Fiyat Hesaplama Nasıl Çalışır?',
      howWorksSteps: [
        { title: 'Proje Tipini Belirleyin', desc: 'Web uygulaması, mobil uygulama, oyun veya hibrit (web + mobil) proje tiplerinden birini seçin. Her proje tipi farklı teknoloji yığını ve geliştirme süreci gerektirir.' },
        { title: 'Sektörünüzü Seçin', desc: 'Eğitim, e-ticaret, sağlık, finans, IoT, lojistik, CRM, restoran gibi 14 farklı sektörden projenize uygun olanı seçin. Her sektörün kendine özgü gereksinimleri maliyeti etkiler.' },
        { title: 'Platform ve Sayfa Sayısını Belirleyin', desc: 'Mobil projeler için iOS, Android veya her iki platform desteğini; web projeleri için sayfa sayısını belirleyin.' },
        { title: 'Tasarım Seviyesini Seçin', desc: 'Temel tasarımdan özel (custom) UI/UX tasarıma kadar 4 farklı tasarım seviyesi arasından projenize uygun olanı seçin.' },
        { title: 'Özellikleri Ekleyin', desc: 'Ödeme entegrasyonu, kimlik doğrulama, API entegrasyonu, canlı sohbet, bildirim sistemi, analitik, video streaming ve IoT cihaz yönetimi gibi özelliklerden ihtiyaç duyduklarınızı ekleyin.' },
        { title: 'Tahmini Bütçenizi Görün', desc: 'Tüm seçimleriniz analiz edilerek size minimum ve maksimum bütçe aralığı sunulur. Detaylı teklif almak için iletişim formunu doldurabilirsiniz.' },
      ],
      pricingTitle: 'Yazılım Geliştirme Fiyatları 2026',
      pricingIntro: 'Yazılım geliştirme maliyetleri; projenin kapsamına, kullanılan teknolojilere, tasarım kalitesine ve geliştirme süresine göre değişkenlik gösterir. Aşağıda 2026 yılı için Türkiye\'deki yazılım geliştirme fiyat aralıkları hakkında genel bir bilgi bulabilirsiniz.',
      pricingCards: [
        { title: 'Web Uygulaması Geliştirme', range: '$500 – $15.000+', desc: 'Kurumsal web siteleri, SaaS platformları, e-ticaret siteleri, yönetim panelleri ve dashboard uygulamaları. React, Angular, Vue.js gibi modern framework\'ler ile geliştirme.' },
        { title: 'Mobil Uygulama Geliştirme', range: '$2.000 – $25.000+', desc: 'iOS ve Android için native veya cross-platform (React Native, Flutter) mobil uygulama geliştirme. Tek platform veya her iki platform desteği.' },
        { title: 'Oyun Geliştirme', range: '$4.000 – $50.000+', desc: '2D ve 3D oyun geliştirme, mobil oyunlar, casual oyunlar ve strateji oyunları. Unity, Unreal Engine gibi oyun motorları ile geliştirme.' },
        { title: 'Eğitim Yazılımı Geliştirme', range: '$3.000 – $20.000+', desc: 'LMS (Öğrenme Yönetim Sistemi), online eğitim platformları, sınav ve değerlendirme sistemleri, sertifika yönetimi ve oyunlaştırma modülleri.' },
        { title: 'E-Ticaret Yazılımı', range: '$2.500 – $18.000+', desc: 'Online mağaza, pazar yeri (marketplace), ödeme entegrasyonu, stok yönetimi, kargo takibi ve müşteri yönetim sistemleri.' },
        { title: 'IoT ve Akıllı Sistem Yazılımı', range: '$5.000 – $30.000+', desc: 'IoT cihaz yönetimi, gerçek zamanlı veri izleme, sensör entegrasyonu, akıllı otomasyon ve endüstriyel IoT çözümleri.' },
      ],
      factorsTitle: 'Yazılım Maliyetini Etkileyen Faktörler',
      factors: [
        { title: 'Proje Kapsamı ve Karmaşıklığı', desc: 'Projenizin fonksiyon sayısı, iş mantığı karmaşıklığı ve veri modeli büyüklüğü maliyeti doğrudan etkiler. Basit bir tanıtım sitesi ile kapsamlı bir ERP sistemi arasında büyük fark bulunur.' },
        { title: 'Kullanılan Teknolojiler', desc: 'React, Angular, Flutter, React Native, Node.js, Python, .NET gibi teknoloji seçimleri; veritabanı tercihleri (PostgreSQL, MongoDB, Firebase); ve bulut altyapısı (AWS, Azure, Google Cloud) maliyeti etkiler.' },
        { title: 'Tasarım ve UI/UX Kalitesi', desc: 'Hazır şablon kullanımından tamamen özel tasarıma kadar geniş bir yelpaze vardır. Kullanıcı araştırması, wireframe, prototipleme ve animasyonlu arayüzler maliyeti artırır.' },
        { title: 'Entegrasyon Gereksinimleri', desc: 'Ödeme sistemleri (iyzico, Stripe), SMS servisleri, e-posta entegrasyonları, harita API\'ları, sosyal medya girişi ve üçüncü parti API entegrasyonları ek maliyet oluşturur.' },
        { title: 'Güvenlik ve Uyumluluk', desc: 'KVKK uyumluluğu, SSL sertifikası, iki faktörlü kimlik doğrulama, veri şifreleme ve düzenli güvenlik testleri projenizin güvenlik ihtiyaçlarına göre maliyet ekler.' },
        { title: 'Bakım ve Destek', desc: 'Yazılım geliştirme sonrası bakım, güncelleme, sunucu yönetimi, hata düzeltme ve teknik destek hizmetleri yıllık maliyetleri etkiler.' },
      ],
      sectorTitle: 'Sektörlere Göre Yazılım Çözümleri',
      sectors: [
        { title: 'Eğitim Sektörü Yazılımları', desc: 'Online eğitim platformları, LMS sistemleri, sanal sınıf uygulamaları, öğrenci takip sistemleri, sınav ve değerlendirme modülleri, sertifika yönetimi. MEB uyumlu yazılımlar ve üniversite yönetim sistemleri.' },
        { title: 'Sağlık Sektörü Yazılımları', desc: 'Hastane bilgi yönetim sistemleri (HBYS), randevu ve hasta takip uygulamaları, telemedikal platformlar, e-reçete entegrasyonu, tıbbi görüntüleme yazılımları ve sağlık verisi analitik çözümleri.' },
        { title: 'Finans ve Fintech Yazılımları', desc: 'Dijital bankacılık uygulamaları, ödeme sistemleri, kripto para platformları, portföy yönetimi, risk analizi, muhasebe ve fatura yönetim yazılımları.' },
        { title: 'Lojistik ve Taşımacılık Yazılımları', desc: 'Filo yönetim sistemleri, rota optimizasyonu, depo yönetimi (WMS), kargo takip uygulamaları, teslimat planlama ve tedarik zinciri yönetim yazılımları.' },
        { title: 'Restoran ve Yeme-İçme Yazılımları', desc: 'Online sipariş ve menü yönetimi, masa rezervasyon sistemleri, QR kodlu menü uygulamaları, mutfak yönetim ekranları, paket servis entegrasyonu ve müşteri sadakat programları.' },
        { title: 'İnsan Kaynakları ve CRM Yazılımları', desc: 'Personel yönetim sistemi, bordro ve izin takibi, performans değerlendirme, işe alım süreç yönetimi, müşteri ilişkileri yönetimi (CRM) ve satış takip uygulamaları.' },
      ],
      faqTitle: 'Sık Sorulan Sorular',
      faqs: [
        { q: 'Yazılım maliyet hesaplayıcı nedir?', a: 'Yazılım maliyet hesaplayıcı, bir yazılım projesinin tahmini bütçesini belirlemek için kullanılan interaktif bir araçtır. Proje tipi, sektör, platform, tasarım seviyesi ve özellik gereksinimleri gibi parametreleri analiz ederek projenizin yaklaşık maliyetini hesaplar. ProgenX\'in hesaplayıcısı 2026 yılı güncel piyasa fiyatlarını baz almaktadır.' },
        { q: 'Yazılım geliştirme ne kadar sürer?', a: 'Yazılım geliştirme süresi projenin kapsamına göre değişir. Basit bir web sitesi 2-4 haftada, orta ölçekli bir web uygulaması 1-3 ayda, kapsamlı bir mobil uygulama 2-6 ayda, kurumsal yazılım projeleri ise 3-12 ayda tamamlanabilir. Agile metodoloji ile çalışarak süreç daha verimli yönetilir.' },
        { q: 'Web uygulaması geliştirme fiyatları ne kadardır?', a: 'Web uygulaması geliştirme maliyetleri projenin karmaşıklığına göre değişir. Temel kurumsal web sitesi $500-$3.000, orta ölçekli web uygulaması $3.000-$8.000, kapsamlı SaaS platformu ise $8.000-$15.000+ aralığında olabilir. Fiyat; sayfa sayısı, özel tasarım, entegrasyon ve ek özellik ihtiyaçlarına göre değişir.' },
        { q: 'Mobil uygulama geliştirme maliyeti ne kadar?', a: 'Mobil uygulama maliyetleri platforma ve karmaşıklığa göre değişir. Tek platform (iOS veya Android) için $2.000-$10.000, her iki platform için $5.000-$25.000 aralığında olabilir. React Native veya Flutter ile cross-platform geliştirme, her iki platforma ayrı ayrı native geliştirmeye göre maliyet avantajı sağlar.' },
        { q: 'Eğitim yazılımı geliştirme maliyeti nedir?', a: 'Eğitim yazılımı maliyetleri içerdiği özelliklere göre değişir. Temel bir LMS (Öğrenme Yönetim Sistemi) $3.000-$8.000, video streaming destekli online eğitim platformu $8.000-$15.000, gamification ve sertifika sistemi dahil kapsamlı bir eğitim platformu ise $15.000-$30.000+ aralığında olabilir.' },
        { q: 'E-ticaret sitesi yaptırma fiyatları ne kadardır?', a: 'E-ticaret sitesi maliyetleri mağaza türüne ve ölçeğine bağlıdır. Temel online mağaza $2.500-$5.000, ödeme ve kargo entegrasyonlu orta ölçekli e-ticaret $5.000-$10.000, çoklu satıcılı marketplace ise $10.000-$25.000+ aralığında olabilir. iyzico, PayTR gibi ödeme entegrasyonları ek maliyet oluşturur.' },
        { q: 'IoT yazılımı ne kadar tutar?', a: 'IoT (Nesnelerin İnterneti) yazılım maliyetleri projenin ölçeğine göre değişir. Basit sensör izleme uygulaması $5.000-$10.000, endüstriyel IoT dashboard $10.000-$20.000, kapsamlı akıllı fabrika veya akıllı şehir çözümleri ise $20.000-$50.000+ aralığında olabilir.' },
        { q: 'Oyun geliştirme maliyetleri ne kadar?', a: 'Oyun geliştirme maliyetleri oyun türüne ve platformuna göre büyük farklılık gösterir. Casual mobil oyun $4.000-$10.000, 2D oyun $5.000-$15.000, 3D oyun $10.000-$30.000+, strateji oyunu ise $8.000-$25.000 aralığında olabilir. Unity ve Unreal Engine gibi oyun motorları tercih edilmektedir.' },
        { q: 'Yazılım projemde hangi teknolojiler kullanılır?', a: 'ProgenX olarak modern ve güncel teknolojiler kullanıyoruz. Frontend: React, Next.js, Vue.js, Angular. Mobil: React Native, Flutter, Swift, Kotlin. Backend: Node.js, Python, .NET, Java. Veritabanı: PostgreSQL, MongoDB, Firebase, MySQL. Bulut: AWS, Google Cloud, Azure. DevOps: Docker, Kubernetes, CI/CD.' },
        { q: 'Yazılım geliştirme sürecinde neler yapılır?', a: 'Profesyonel yazılım geliştirme süreci şu aşamalardan oluşur: 1) İhtiyaç Analizi ve Planlama, 2) UI/UX Tasarımı, 3) Prototipleme, 4) Frontend ve Backend Geliştirme, 5) Entegrasyon ve Test, 6) Deployment (Yayınlama), 7) Bakım ve Destek. Agile/Scrum metodolojisi ile iteratif geliştirme yapılır.' },
        { q: 'Yazılım projem için bakım ve destek dahil mi?', a: 'Evet, ProgenX olarak proje tesliminden sonra belirli süre ücretsiz bakım ve destek sağlıyoruz. Sonrasında aylık veya yıllık bakım paketleri sunuyoruz. Bakım kapsamında: hata düzeltme, güvenlik güncellemeleri, performans optimizasyonu, küçük özellik eklemeleri ve teknik destek yer alır.' },
        { q: 'Hesaplayıcıdaki fiyatlar kesin mi?', a: 'Hayır, hesaplayıcımızın verdiği fiyatlar tahmini bütçe aralıklarıdır. Gerçek proje maliyeti; detaylı ihtiyaç analizi, özel tasarım gereksinimleri, entegrasyon karmaşıklığı ve proje takvimi gibi faktörlere göre değişebilir. Kesin fiyat için ücretsiz teklif talep edebilirsiniz.' },
      ],
      ctaTitle: 'Projeniz İçin Ücretsiz Teklif Alın',
      ctaDesc: 'Yazılım projenizin detaylarını paylaşın, size özel fiyat teklifi hazırlayalım. İlk görüşme ve ihtiyaç analizi tamamen ücretsizdir.',
      ctaBtn: 'Hemen Teklif Alın',
    },
  },
  en: {
    heroTitle: 'SOFTWARE COST CALCULATOR',
    pageTitle: 'Software Cost Calculator | ProgenX',
    metaDesc: 'ProgenX software project cost calculator. Estimate the budget for your web, mobile, and game projects.',
    introTitle: 'Calculate Your Software Project Cost',
    introDesc: 'Find out the estimated budget for your project in a few simple steps. Select your project type, features, and needs.',
    startBtn: 'Start Calculating',
    step: 'Step',
    next: 'Next',
    back: 'Back',
    calculate: 'Calculate',
    loadingTitle: 'Analyzing Your Project...',
    loadingDesc: 'Processing your data for the most accurate estimate.',
    resultTitle: 'Your Estimated Project Budget',
    resultNote: 'This estimate is automatically calculated based on your selections. Final price may vary depending on project details.',
    contactTitle: 'Get a Detailed Quote',
    contactDesc: 'Leave your details and we\'ll prepare a custom quote for you.',
    nameLabel: 'Full Name',
    emailLabel: 'Email Address',
    phoneLabel: 'Phone Number',
    messageLabel: 'Notes About Your Project (Optional)',
    sendBtn: 'Request Quote',
    successTitle: 'Request Received!',
    successDesc: 'We will get back to you as soon as possible.',
    restart: 'Recalculate',
    steps: {
      projectType: { label: 'Project Type', title: 'Select Your Project Type' },
      domain: { label: 'Industry', title: 'Select Your Industry' },
      mobilePlatform: { label: 'Platform', title: 'Select Mobile Platform' },
      pageCount: { label: 'Pages', title: 'Select Page Count' },
      gameType: { label: 'Game Type', title: 'Select Game Type' },
      designLevel: { label: 'Design', title: 'Select Design Level' },
      features: { label: 'Features', title: 'Select Features You Need' },
    },
    projectTypes: [
      { id: 'web', name: 'Web Application', price: 300 },
      { id: 'mobile', name: 'Mobile Application', price: 1500 },
      { id: 'game', name: 'Game', price: 5000 },
      { id: 'mobile-game', name: 'Mobile Game', price: 4000 },
      { id: 'both', name: 'Web + Mobile', price: 1800 },
    ],
    domains: [
      { id: 'general', name: 'General', price: 0 },
      { id: 'education', name: 'Education', price: 300 },
      { id: 'iot', name: 'IoT', price: 1000 },
      { id: 'dashboard', name: 'Dashboard', price: 600 },
      { id: 'ecommerce', name: 'E-Commerce', price: 1000 },
      { id: 'finance', name: 'Finance', price: 1500 },
      { id: 'healthcare', name: 'Healthcare', price: 1500 },
      { id: 'social', name: 'Social Media', price: 600 },
      { id: 'cms', name: 'Content Management', price: 400 },
      { id: 'hr', name: 'Human Resources', price: 400 },
      { id: 'crm', name: 'CRM', price: 400 },
      { id: 'project-management', name: 'Project Management', price: 500 },
      { id: 'restaurant', name: 'Restaurant', price: 200 },
      { id: 'logistics', name: 'Logistics', price: 1000 },
    ],
    mobilePlatforms: [
      { id: 'ios', name: 'iOS', price: 1000 },
      { id: 'android', name: 'Android', price: 500 },
      { id: 'both', name: 'iOS + Android', price: 1300 },
    ],
    pageCounts: [
      { id: '1-5', name: '1-5 Pages', price: 0 },
      { id: '6-10', name: '6-10 Pages', price: 100 },
      { id: '11-20', name: '11-20 Pages', price: 200 },
      { id: '21-50', name: '21-50 Pages', price: 300 },
      { id: '50+', name: '50+ Pages', price: 1000 },
    ],
    gameTypes: [
      { id: '2d', name: '2D Game', price: 5000 },
      { id: '3d', name: '3D Game', price: 10000 },
      { id: 'casual', name: 'Casual Game', price: 4000 },
      { id: 'strategy', name: 'Strategy Game', price: 8000 },
    ],
    designLevels: [
      { id: 'basic', name: 'Basic Design', price: 100 },
      { id: 'standard', name: 'Standard Design', price: 200 },
      { id: 'premium', name: 'Premium Design', price: 300 },
      { id: 'custom', name: 'Custom Design', price: 5000 },
    ],
    featuresList: [
      { id: 'payment', name: 'Payment Integration', price: 200 },
      { id: 'auth', name: 'Authentication', price: 100 },
      { id: 'integration', name: 'API Integration', price: 1200 },
      { id: 'reporting', name: 'Reporting', price: 500 },
      { id: 'chat', name: 'Live Chat', price: 300 },
      { id: 'notification', name: 'Notifications', price: 300 },
      { id: 'analytics', name: 'Analytics', price: 600 },
      { id: 'video-streaming', name: 'Video Streaming', price: 1500 },
      { id: 'certificate', name: 'Certificate System', price: 200 },
      { id: 'gamification', name: 'Gamification', price: 200 },
      { id: 'iot-device', name: 'IoT Device Management', price: 1000 },
      { id: 'real-time', name: 'Real-Time Data', price: 1500 },
    ],
    seo: {
      whatIsTitle: 'What Is a Software Cost Calculator?',
      whatIsP1: 'A software cost calculator is an interactive tool used to estimate the budget of a software project. It analyzes parameters such as project type, industry, platform preference, design level, and required features to calculate the approximate cost of your project.',
      whatIsP2: 'The ProgenX software cost calculator provides detailed budget estimates for your web application, mobile application, game development, and enterprise software projects based on 2026 global software market prices. Customized pricing is available for 14 different industries including education, e-commerce, healthcare, fintech, IoT, and logistics.',
      howWorksTitle: 'How Does Software Price Calculation Work?',
      howWorksSteps: [
        { title: 'Choose Project Type', desc: 'Select from web application, mobile application, game, or hybrid (web + mobile) project types. Each requires different technology stacks and development processes.' },
        { title: 'Select Your Industry', desc: 'Choose from 14 different industries including education, e-commerce, healthcare, finance, IoT, logistics, CRM, and restaurant.' },
        { title: 'Define Platform & Page Count', desc: 'For mobile projects, select iOS, Android, or both; for web projects, define the page count.' },
        { title: 'Choose Design Level', desc: 'Select from 4 design levels ranging from basic to custom UI/UX design.' },
        { title: 'Add Features', desc: 'Add features you need such as payment integration, authentication, API integration, live chat, notifications, analytics, video streaming, and IoT device management.' },
        { title: 'See Your Estimated Budget', desc: 'All your selections are analyzed to provide minimum and maximum budget ranges. You can fill out the contact form for a detailed quote.' },
      ],
      pricingTitle: 'Software Development Prices 2026',
      pricingIntro: 'Software development costs vary depending on project scope, technologies used, design quality, and development timeline. Below you can find general information about software development price ranges for 2026.',
      pricingCards: [
        { title: 'Web Application Development', range: '$500 – $15,000+', desc: 'Corporate websites, SaaS platforms, e-commerce sites, admin panels, and dashboard applications. Development with modern frameworks like React, Angular, Vue.js.' },
        { title: 'Mobile App Development', range: '$2,000 – $25,000+', desc: 'Native or cross-platform (React Native, Flutter) mobile app development for iOS and Android. Single platform or dual platform support.' },
        { title: 'Game Development', range: '$4,000 – $50,000+', desc: '2D and 3D game development, mobile games, casual games, and strategy games. Development with game engines like Unity, Unreal Engine.' },
        { title: 'Education Software Development', range: '$3,000 – $20,000+', desc: 'LMS (Learning Management System), online education platforms, exam and assessment systems, certificate management, and gamification modules.' },
        { title: 'E-Commerce Software', range: '$2,500 – $18,000+', desc: 'Online stores, marketplaces, payment integration, inventory management, shipment tracking, and customer management systems.' },
        { title: 'IoT & Smart System Software', range: '$5,000 – $30,000+', desc: 'IoT device management, real-time data monitoring, sensor integration, smart automation, and industrial IoT solutions.' },
      ],
      factorsTitle: 'Factors Affecting Software Cost',
      factors: [
        { title: 'Project Scope & Complexity', desc: 'The number of functions, business logic complexity, and data model size directly affect the cost. There is a significant difference between a simple landing page and a comprehensive ERP system.' },
        { title: 'Technologies Used', desc: 'Technology choices like React, Angular, Flutter, React Native, Node.js, Python, .NET; database preferences (PostgreSQL, MongoDB, Firebase); and cloud infrastructure (AWS, Azure, Google Cloud) affect costs.' },
        { title: 'Design & UI/UX Quality', desc: 'There is a wide spectrum from using ready-made templates to fully custom design. User research, wireframing, prototyping, and animated interfaces increase costs.' },
        { title: 'Integration Requirements', desc: 'Payment systems (Stripe, PayPal), SMS services, email integrations, map APIs, social media login, and third-party API integrations create additional costs.' },
        { title: 'Security & Compliance', desc: 'GDPR compliance, SSL certificates, two-factor authentication, data encryption, and regular security testing add costs based on your project\'s security needs.' },
        { title: 'Maintenance & Support', desc: 'Post-development maintenance, updates, server management, bug fixes, and technical support services affect annual costs.' },
      ],
      sectorTitle: 'Software Solutions by Industry',
      sectors: [
        { title: 'Education Sector Software', desc: 'Online education platforms, LMS systems, virtual classroom applications, student tracking systems, exam and assessment modules, certificate management.' },
        { title: 'Healthcare Sector Software', desc: 'Hospital information management systems, appointment and patient tracking applications, telemedicine platforms, e-prescription integration, medical imaging software.' },
        { title: 'Finance & Fintech Software', desc: 'Digital banking applications, payment systems, cryptocurrency platforms, portfolio management, risk analysis, accounting and invoice management software.' },
        { title: 'Logistics & Transportation Software', desc: 'Fleet management systems, route optimization, warehouse management (WMS), cargo tracking applications, delivery planning and supply chain management.' },
        { title: 'Restaurant & F&B Software', desc: 'Online ordering and menu management, table reservation systems, QR code menu applications, kitchen management screens, delivery integration.' },
        { title: 'HR & CRM Software', desc: 'Personnel management system, payroll and leave tracking, performance evaluation, recruitment process management, customer relationship management (CRM).' },
      ],
      faqTitle: 'Frequently Asked Questions',
      faqs: [
        { q: 'What is a software cost calculator?', a: 'A software cost calculator is an interactive tool for estimating a software project\'s budget. It analyzes parameters like project type, industry, platform, design level, and feature requirements. ProgenX\'s calculator is based on 2026 current market prices.' },
        { q: 'How long does software development take?', a: 'Software development time varies by scope. A simple website takes 2-4 weeks, a medium web application 1-3 months, a comprehensive mobile app 2-6 months, and enterprise projects 3-12 months. Agile methodology makes the process more efficient.' },
        { q: 'How much does web application development cost?', a: 'Web application development costs vary by complexity. Basic corporate website $500-$3,000, medium web application $3,000-$8,000, comprehensive SaaS platform $8,000-$15,000+. Price varies by page count, custom design, integration, and additional feature needs.' },
        { q: 'How much does mobile app development cost?', a: 'Mobile app costs vary by platform and complexity. Single platform (iOS or Android) $2,000-$10,000, both platforms $5,000-$25,000. Cross-platform development with React Native or Flutter provides cost advantages over separate native development.' },
        { q: 'How much does education software development cost?', a: 'Education software costs vary by features. Basic LMS $3,000-$8,000, video streaming-supported online education platform $8,000-$15,000, comprehensive platform with gamification and certification $15,000-$30,000+.' },
        { q: 'How much does an e-commerce site cost?', a: 'E-commerce site costs depend on store type and scale. Basic online store $2,500-$5,000, medium e-commerce with payment and shipping integration $5,000-$10,000, multi-vendor marketplace $10,000-$25,000+.' },
        { q: 'How much does IoT software cost?', a: 'IoT software costs vary by project scale. Simple sensor monitoring app $5,000-$10,000, industrial IoT dashboard $10,000-$20,000, comprehensive smart factory or smart city solutions $20,000-$50,000+.' },
        { q: 'How much does game development cost?', a: 'Game development costs vary significantly by game type and platform. Casual mobile game $4,000-$10,000, 2D game $5,000-$15,000, 3D game $10,000-$30,000+, strategy game $8,000-$25,000. Game engines like Unity and Unreal Engine are preferred.' },
        { q: 'What technologies are used in my software project?', a: 'At ProgenX, we use modern technologies. Frontend: React, Next.js, Vue.js, Angular. Mobile: React Native, Flutter, Swift, Kotlin. Backend: Node.js, Python, .NET, Java. Database: PostgreSQL, MongoDB, Firebase, MySQL. Cloud: AWS, Google Cloud, Azure.' },
        { q: 'What happens during the software development process?', a: 'Professional software development consists of: 1) Requirements Analysis & Planning, 2) UI/UX Design, 3) Prototyping, 4) Frontend & Backend Development, 5) Integration & Testing, 6) Deployment, 7) Maintenance & Support. Iterative development with Agile/Scrum methodology.' },
        { q: 'Is maintenance and support included?', a: 'Yes, ProgenX provides complimentary maintenance and support for a period after project delivery. We then offer monthly or annual maintenance packages including bug fixes, security updates, performance optimization, minor feature additions, and technical support.' },
        { q: 'Are the calculator prices final?', a: 'No, the prices provided by our calculator are estimated budget ranges. Actual project cost may vary based on detailed requirements analysis, custom design needs, integration complexity, and project timeline. You can request a free quote for exact pricing.' },
      ],
      ctaTitle: 'Get a Free Quote for Your Project',
      ctaDesc: 'Share the details of your software project and we\'ll prepare a custom price quote. The first consultation and requirements analysis is completely free.',
      ctaBtn: 'Get a Quote Now',
    },
  },
};

const STEP_KEYS = ['projectType', 'domain', 'mobilePlatform', 'pageCount', 'gameType', 'designLevel', 'features'];

const SoftwareCostCalculator = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en');
  const t = isEnglish ? TEXTS.en : TEXTS.tr;
  const canonicalUrl = isEnglish
    ? 'https://progenx.com.tr/en/software-calculator'
    : 'https://progenx.com.tr/yazilim-hesaplayici';

  const [started, setStarted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState({
    projectType: null,
    domain: null,
    mobilePlatform: null,
    pageCount: null,
    gameType: null,
    designLevel: null,
    features: [],
  });
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [formSent, setFormSent] = useState(false);

  const getActiveSteps = useCallback(() => {
    const pt = selections.projectType;
    return STEP_KEYS.filter((key) => {
      if (key === 'mobilePlatform') return pt === 'mobile' || pt === 'both' || pt === 'mobile-game';
      if (key === 'pageCount') return pt === 'web' || pt === 'both';
      if (key === 'gameType') return pt === 'game' || pt === 'mobile-game';
      return true;
    });
  }, [selections.projectType]);

  const activeSteps = getActiveSteps();
  const currentKey = activeSteps[currentStep];

  const getStepOptions = () => {
    switch (currentKey) {
      case 'projectType': return t.projectTypes;
      case 'domain': return t.domains;
      case 'mobilePlatform': return t.mobilePlatforms;
      case 'pageCount': return t.pageCounts;
      case 'gameType': return t.gameTypes;
      case 'designLevel': return t.designLevels;
      default: return [];
    }
  };

  const scrollToCalculator = useCallback(() => {
    setTimeout(() => {
      const el = document.querySelector('.calculator-step-card');
      if (el) {
        const rect = el.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetY = scrollTop + rect.top - (window.innerHeight / 2 - rect.height / 2);
        window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
      }
    }, 50);
  }, []);

  const handleSelect = (id) => {
    if (currentKey === 'projectType') {
      setSelections((prev) => ({
        ...prev,
        projectType: id,
        mobilePlatform: null,
        pageCount: null,
        gameType: null,
      }));
    } else {
      setSelections((prev) => ({ ...prev, [currentKey]: id }));
    }
    scrollToCalculator();
  };

  const handleFeatureToggle = (id) => {
    setSelections((prev) => {
      const features = prev.features.includes(id)
        ? prev.features.filter((f) => f !== id)
        : [...prev.features, id];
      return { ...prev, features };
    });
  };

  const canProceed = () => {
    if (currentKey === 'features') return true;
    return selections[currentKey] !== null;
  };

  const handleNext = () => {
    if (currentStep < activeSteps.length - 1) {
      setCurrentStep((s) => s + 1);
      scrollToCalculator();
    } else {
      setLoading(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
      scrollToCalculator();
    }
  };

  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => {
      setLoading(false);
      setShowResult(true);
    }, 15000);
    return () => clearTimeout(timer);
  }, [loading]);

  const calculateTotal = () => {
    let total = 0;
    const findPrice = (list, id) => {
      const item = list.find((o) => o.id === id);
      return item ? item.price : 0;
    };
    total += findPrice(t.projectTypes, selections.projectType);
    total += findPrice(t.domains, selections.domain);
    if (selections.mobilePlatform) total += findPrice(t.mobilePlatforms, selections.mobilePlatform);
    if (selections.pageCount) total += findPrice(t.pageCounts, selections.pageCount);
    if (selections.gameType) total += findPrice(t.gameTypes, selections.gameType);
    total += findPrice(t.designLevels, selections.designLevel);
    selections.features.forEach((fId) => {
      total += findPrice(t.featuresList, fId);
    });
    return total;
  };

  const [formSending, setFormSending] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setFormSending(true);

    const lambdaUrl = process.env.REACT_APP_LAMBDA_CONTACT_URL;

    if (!lambdaUrl) {
      setTimeout(() => {
        setFormSent(true);
        setFormSending(false);
      }, 1000);
      return;
    }

    try {
      const total = calculateTotal();
      await fetch(lambdaUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({
          name: contactForm.name,
          email: contactForm.email,
          phone: contactForm.phone,
          message: contactForm.message,
          source: 'calculator',
          estimateMin: Math.round(total * 0.8),
          estimateMax: Math.round(total * 1.3),
          selections: selections,
        }),
      });
      setFormSent(true);
    } catch (error) {
      console.error('Form Error:', error);
      setFormSent(true);
    } finally {
      setFormSending(false);
    }
  };

  const handleRestart = () => {
    setStarted(false);
    setCurrentStep(0);
    setSelections({ projectType: null, domain: null, mobilePlatform: null, pageCount: null, gameType: null, designLevel: null, features: [] });
    setLoading(false);
    setShowResult(false);
    setContactForm({ name: '', email: '', phone: '', message: '' });
    setFormSent(false);
  };

  const formatUSD = (n) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  const total = calculateTotal();
  const low = Math.round(total * 0.8);
  const high = Math.round(total * 1.3);
  const progressPercent = activeSteps.length > 1 ? (currentStep / (activeSteps.length - 1)) * 100 : 0;

  const renderStepIndicator = () => (
    <div className="calculator-progress">
      <div className="calculator-steps-indicator">
        {activeSteps.map((key, i) => {
          let cls = 'upcoming';
          if (i < currentStep) cls = 'completed';
          else if (i === currentStep) cls = 'active';
          const canClick = i < currentStep;
          return (
            <React.Fragment key={key}>
              {i > 0 && <div className={`calculator-step-line ${cls === 'upcoming' ? 'upcoming' : i <= currentStep ? (i === currentStep ? 'active' : 'completed') : 'upcoming'}`} />}
              <div
                className={`calculator-step-dot ${cls} ${canClick ? 'clickable' : ''}`}
                onClick={() => { if (canClick) { setCurrentStep(i); scrollToCalculator(); } }}
                role={canClick ? 'button' : undefined}
                tabIndex={canClick ? 0 : undefined}
              >
                {i < currentStep ? <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '0.7rem' }} /> : i + 1}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <div className="calculator-progress-bar">
        <div className="calculator-progress-fill" style={{ width: `${progressPercent}%` }} />
      </div>
    </div>
  );

  const renderOptions = () => {
    const options = getStepOptions();
    return (
      <div className="calculator-options">
        {options.map((opt) => (
          <div
            key={opt.id}
            className={`calculator-option ${selections[currentKey] === opt.id ? 'selected' : ''}`}
            onClick={() => handleSelect(opt.id)}
          >
            <div className="calculator-option-name">{opt.name}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderFeatures = () => (
    <div className="calculator-features-grid">
      {t.featuresList.map((feat) => (
        <div
          key={feat.id}
          className={`calculator-feature ${selections.features.includes(feat.id) ? 'selected' : ''}`}
          onClick={() => handleFeatureToggle(feat.id)}
        >
          <FontAwesomeIcon icon={faCheckCircle} className="calculator-feature-check" />
          <div className="calculator-feature-info">
            <div className="calculator-feature-name">{feat.name}</div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderStep = () => {
    const stepInfo = t.steps[currentKey];
    const isLast = currentStep === activeSteps.length - 1;
    return (
      <div className="calculator-step-card">
        <div className="calculator-step-label">{t.step} {currentStep + 1}/{activeSteps.length} — {stepInfo.label}</div>
        <h3>{stepInfo.title}</h3>
        {currentKey === 'features' ? renderFeatures() : renderOptions()}
        <div className="calculator-nav">
          {currentStep > 0 && (
            <button className="calculator-btn calculator-btn-back" onClick={handleBack}>
              <FontAwesomeIcon icon={faArrowLeft} /> {t.back}
            </button>
          )}
          <button className="calculator-btn calculator-btn-next" onClick={handleNext} disabled={!canProceed()}>
            {isLast ? t.calculate : t.next} <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>
      </div>
    );
  };

  const renderLoading = () => (
    <div className="calculator-loading">
      <div className="calculator-loading-spinner">
        <FontAwesomeIcon icon={faSpinner} />
      </div>
      <h3>{t.loadingTitle}</h3>
      <p>{t.loadingDesc}</p>
      <div className="calculator-loading-bar">
        <div className="calculator-loading-bar-fill" />
      </div>
    </div>
  );

  const renderResult = () => {
    const getName = (list, id) => { const it = list.find((o) => o.id === id); return it ? it.name : ''; };
    return (
      <div className="calculator-result">
        <div className="calculator-result-card">
          <h3>{t.resultTitle}</h3>
          <div className="calculator-result-range">{formatUSD(low)} — {formatUSD(high)}</div>
          <p className="calculator-result-note">{t.resultNote}</p>
          <div className="calculator-result-breakdown">
            <div className="calculator-breakdown-item">
              <span className="calculator-breakdown-label">{t.steps.projectType.label}:</span>
              <span className="calculator-breakdown-value">{getName(t.projectTypes, selections.projectType)}</span>
            </div>
            <div className="calculator-breakdown-item">
              <span className="calculator-breakdown-label">{t.steps.domain.label}:</span>
              <span className="calculator-breakdown-value">{getName(t.domains, selections.domain)}</span>
            </div>
            <div className="calculator-breakdown-item">
              <span className="calculator-breakdown-label">{t.steps.designLevel.label}:</span>
              <span className="calculator-breakdown-value">{getName(t.designLevels, selections.designLevel)}</span>
            </div>
            {selections.features.length > 0 && (
              <div className="calculator-breakdown-item">
                <span className="calculator-breakdown-label">{t.steps.features.label}:</span>
                <span className="calculator-breakdown-value">{selections.features.length}</span>
              </div>
            )}
          </div>
        </div>

        <div className="calculator-contact-form">
          {formSent ? (
            <div className="calculator-form-success">
              <FontAwesomeIcon icon={faCheckCircle} />
              <h4>{t.successTitle}</h4>
              <p>{t.successDesc}</p>
            </div>
          ) : (
            <>
              <h3>{t.contactTitle}</h3>
              <p>{t.contactDesc}</p>
              <form onSubmit={handleContactSubmit}>
                <div className="calculator-form-group">
                  <label>{t.nameLabel}</label>
                  <input type="text" required value={contactForm.name} onChange={(e) => setContactForm((p) => ({ ...p, name: e.target.value }))} />
                </div>
                <div className="calculator-form-group">
                  <label>{t.emailLabel}</label>
                  <input type="email" required value={contactForm.email} onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))} />
                </div>
                <div className="calculator-form-group">
                  <label>{t.phoneLabel}</label>
                  <input type="tel" value={contactForm.phone} onChange={(e) => setContactForm((p) => ({ ...p, phone: e.target.value }))} />
                </div>
                <div className="calculator-form-group">
                  <label>{t.messageLabel}</label>
                  <textarea value={contactForm.message} onChange={(e) => setContactForm((p) => ({ ...p, message: e.target.value }))} />
                </div>
                <button type="submit" className="calculator-form-submit" disabled={formSending}>
                  {formSending ? (isEnglish ? 'Sending...' : 'Gönderiliyor...') : t.sendBtn}
                </button>
              </form>
            </>
          )}
        </div>

        <button className="calculator-restart-btn" onClick={handleRestart}>
          <FontAwesomeIcon icon={faArrowLeft} /> {t.restart}
        </button>
      </div>
    );
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: t.seo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: { '@type': 'Answer', text: faq.a },
    })),
  };

  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: isEnglish ? 'ProgenX Software Cost Calculator' : 'ProgenX Yazılım Maliyet Hesaplayıcı',
    url: canonicalUrl,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'All',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    creator: {
      '@type': 'Organization',
      name: 'ProgenX',
      url: 'https://progenx.com.tr',
    },
    description: t.metaDesc,
  };

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="calculator-page">
      <Helmet>
        <title>{t.pageTitle}</title>
        <meta name="description" content={isEnglish
          ? 'Software cost calculator 2026. Calculate web app, mobile app, game, education software, e-commerce and IoT project budgets. Free estimate from ProgenX.'
          : 'Yazılım maliyet hesaplayıcı 2026. Web uygulaması, mobil uygulama, oyun, eğitim yazılımı, e-ticaret ve IoT proje bütçenizi hesaplayın. ProgenX\'ten ücretsiz tahmin alın.'
        } />
        <meta name="keywords" content={isEnglish
          ? 'software cost calculator, software development prices, web app cost, mobile app cost, game development cost, education software cost, e-commerce development, IoT software price, ProgenX'
          : 'yazılım maliyet hesaplayıcı, yazılım geliştirme fiyatları, web uygulama maliyeti, mobil uygulama maliyeti, oyun geliştirme maliyeti, eğitim yazılımı fiyatı, e-ticaret yazılımı, IoT yazılım fiyatı, yazılım fiyat hesaplama, yazılım projesi bütçesi, ProgenX'
        } />
        <meta property="og:title" content={t.pageTitle} />
        <meta property="og:description" content={t.metaDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="ProgenX" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.pageTitle} />
        <meta name="twitter:description" content={t.metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(webAppSchema)}</script>
      </Helmet>

      <Hero title={t.heroTitle} backgroundImage={projectsHeroImage} />

      <div className="calculator-container">
        {!started && !loading && !showResult && (
          <div className="calculator-intro">
            <h2>{t.introTitle}</h2>
            <p>{t.introDesc}</p>
            <button className="calculator-start-btn" onClick={() => { setStarted(true); scrollToCalculator(); }}>
              {t.startBtn} <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
        )}
        {started && !loading && !showResult && (
          <>
            {renderStepIndicator()}
            {renderStep()}
          </>
        )}
        {loading && renderLoading()}
        {showResult && renderResult()}
      </div>

      <div className="calc-seo-content">
        <section className="calc-seo-section">
          <h2>{t.seo.whatIsTitle}</h2>
          <p>{t.seo.whatIsP1}</p>
          <p>{t.seo.whatIsP2}</p>
        </section>

        <section className="calc-seo-section">
          <h2>{t.seo.howWorksTitle}</h2>
          <div className="calc-how-steps">
            {t.seo.howWorksSteps.map((step, i) => (
              <div key={i} className="calc-how-step">
                <div className="calc-how-step-num">{i + 1}</div>
                <div className="calc-how-step-content">
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="calc-seo-section">
          <h2>{t.seo.pricingTitle}</h2>
          <p className="calc-seo-intro">{t.seo.pricingIntro}</p>
          <div className="calc-pricing-grid">
            {t.seo.pricingCards.map((card, i) => (
              <div key={i} className="calc-pricing-card">
                <h3>{card.title}</h3>
                <div className="calc-pricing-range">{card.range}</div>
                <p>{card.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="calc-seo-section">
          <h2>{t.seo.factorsTitle}</h2>
          <div className="calc-factors-grid">
            {t.seo.factors.map((factor, i) => (
              <div key={i} className="calc-factor-card">
                <h3>{factor.title}</h3>
                <p>{factor.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="calc-seo-section">
          <h2>{t.seo.sectorTitle}</h2>
          <div className="calc-sectors-grid">
            {t.seo.sectors.map((sector, i) => (
              <div key={i} className="calc-sector-card">
                <h3>{sector.title}</h3>
                <p>{sector.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="calc-seo-section calc-faq-section">
          <h2>{t.seo.faqTitle}</h2>
          <div className="calc-faq-list">
            {t.seo.faqs.map((faq, i) => (
              <div key={i} className={`calc-faq-item ${openFaq === i ? 'open' : ''}`}>
                <button className="calc-faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{faq.q}</span>
                  <span className="calc-faq-icon">{openFaq === i ? '−' : '+'}</span>
                </button>
                {openFaq === i && <div className="calc-faq-answer"><p>{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </section>

        <section className="calc-seo-cta">
          <h2>{t.seo.ctaTitle}</h2>
          <p>{t.seo.ctaDesc}</p>
          <a href={isEnglish ? '/en/contact' : '/iletisim'} className="calc-seo-cta-btn">
            {t.seo.ctaBtn} <FontAwesomeIcon icon={faArrowRight} />
          </a>
        </section>
      </div>
    </div>
  );
};

export default SoftwareCostCalculator;
